import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

interface UseRecaptchaOptions {
  siteKey: string;
  action?: string;
}

export function useRecaptcha({ siteKey, action = 'submit' }: UseRecaptchaOptions) {
  const scriptLoaded = useRef(false);
  const scriptId = 'recaptcha-v3-script';

  useEffect(() => {
    // Skip if no site key provided
    if (!siteKey) {
      console.warn('reCAPTCHA site key not provided');
      return;
    }

    // Check if script is already loaded
    if (document.getElementById(scriptId)) {
      if (window.grecaptcha) {
        scriptLoaded.current = true;
      }
      return;
    }

    // Check if grecaptcha is already available (loaded by another component)
    if (window.grecaptcha) {
      scriptLoaded.current = true;
      return;
    }

    // Load reCAPTCHA script
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      // Wait a moment for grecaptcha to initialize
      const checkGrecaptcha = setInterval(() => {
        if (window.grecaptcha) {
          clearInterval(checkGrecaptcha);
          window.grecaptcha.ready(() => {
            scriptLoaded.current = true;
          });
        }
      }, 100);
      
      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkGrecaptcha);
        if (!window.grecaptcha) {
          console.error('❌ reCAPTCHA failed to load after 5 seconds. Check your site key and domain configuration.');
        }
      }, 5000);
    };

    script.onerror = () => {
      console.error('Failed to load reCAPTCHA script');
    };

    document.head.appendChild(script);

    return () => {
      // Don't remove script on unmount as other components might be using it
    };
  }, [siteKey]);

  const executeRecaptcha = async (): Promise<string | null> => {
    if (!siteKey) {
      console.warn('reCAPTCHA site key not provided');
      return null;
    }

    if (!window.grecaptcha) {
      console.warn('reCAPTCHA not loaded yet, waiting...');
      // Wait a bit and try again
      await new Promise(resolve => setTimeout(resolve, 500));
      if (!window.grecaptcha) {
        console.error('reCAPTCHA still not available after waiting');
        return null;
      }
    }

    try {
      // Ensure grecaptcha is ready
      return new Promise((resolve) => {
        window.grecaptcha.ready(async () => {
          try {
            const token = await window.grecaptcha.execute(siteKey, { action });
            resolve(token);
          } catch (error) {
            console.error('reCAPTCHA execution error:', error);
            resolve(null);
          }
        });
      });
    } catch (error) {
      console.error('reCAPTCHA execution error:', error);
      return null;
    }
  };

  // Read during render, so it must also work in the build-time prerender (no window there).
  return { executeRecaptcha, isLoaded: scriptLoaded.current || (typeof window !== 'undefined' && !!window.grecaptcha) };
}

